import { useEffect, useRef, useState } from "react";
import { motion, useAnimationControls } from "framer-motion";
import { Droplet, Plug, Power } from "lucide-react";
import { api } from "~/lib/api";
import { encodeSetActuator } from "~/lib/proto";
import { Switch } from "~/components/ui/Switch";
import { IconBadge, type IconBadgeTint } from "~/components/ui/IconBadge";

export type ValveControlProps = {
  deviceId: string;
  pin: number;
  label: string;
  ackedOn: boolean;
  lastSeenAt: number;
  online: boolean;
  kind?: "valve" | "ac" | "generic";
  onError?: (msg: string) => void;
};

const ACK_TIMEOUT_MS = 35_000;

type Intent = { on: boolean; sentAt: number };

export function ValveControl({
  deviceId,
  pin,
  label,
  ackedOn,
  lastSeenAt,
  online,
  kind = "generic",
  onError,
}: ValveControlProps) {
  const [intent, setIntent] = useState<Intent | null>(null);
  const controls = useAnimationControls();

  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  });

  useEffect(() => {
    if (!intent) return;
    if (lastSeenAt > intent.sentAt) {
      if (ackedOn === intent.on) {
        setIntent(null);
      } else {
        setIntent(null);
        controls.start({ x: [0, -4, 4, -2, 2, 0], transition: { duration: 0.35 } });
        onErrorRef.current?.("The device didn't acknowledge. Try again.");
      }
    }
  }, [ackedOn, lastSeenAt, intent, controls]);

  useEffect(() => {
    if (!intent) return;
    const t = window.setTimeout(() => {
      setIntent(null);
      controls.start({ x: [0, -4, 4, -2, 2, 0], transition: { duration: 0.35 } });
      onErrorRef.current?.("Couldn't reach your device. It may be offline.");
    }, ACK_TIMEOUT_MS);
    return () => window.clearTimeout(t);
  }, [intent, controls]);

  useEffect(() => {
    if (!intent || online) return;
    setIntent(null);
    controls.start({ x: [0, -4, 4, -2, 2, 0], transition: { duration: 0.35 } });
    onErrorRef.current?.("Device went offline. Try again when it reconnects.");
  }, [online, intent, controls]);

  const visualOn = intent?.on ?? ackedOn;
  const sending = intent !== null;
  const interactable = online && !sending;

  async function toggle(next: boolean) {
    if (!interactable) return;
    setIntent({ on: next, sentAt: Date.now() });
    try {
      const payload = encodeSetActuator(pin, next);
      await api.sendCommand(deviceId, payload);
    } catch {
      setIntent(null);
      controls.start({ x: [0, -4, 4, -2, 2, 0], transition: { duration: 0.35 } });
      onErrorRef.current?.("Couldn't send the command.");
    }
  }

  const tint: IconBadgeTint = kind === "valve" ? "blue" : kind === "ac" ? "orange" : "gray";
  const Icon = kind === "valve" ? Droplet : kind === "ac" ? Plug : Power;
  const stateLabel = !online ? "Offline" : sending ? "Sending…" : visualOn ? "On" : "Off";

  const badgeTint: IconBadgeTint = !online ? "gray" : visualOn ? "green" : tint;
  const subtitleColor = !online
    ? "var(--label-tertiary)"
    : visualOn
      ? "var(--system-green)"
      : "var(--label-secondary)";

  return (
    <motion.div
      animate={controls}
      className="flex items-center gap-3 px-4 py-3"
      style={{ opacity: online ? 1 : 0.55, transition: "opacity 200ms var(--ease-soft)" }}
    >
      <IconBadge tint={badgeTint} size={28}>
        <Icon size={15} />
      </IconBadge>
      <div className="flex-1 min-w-0">
        <div className="text-body truncate" style={{ color: "var(--label-primary)" }}>
          {label}
        </div>
        <div className="text-footnote mt-0.5" style={{ color: subtitleColor }}>
          {stateLabel}
        </div>
      </div>
      <Switch
        checked={visualOn}
        onCheckedChange={toggle}
        aria-label={`Toggle ${label}`}
        disabled={!interactable}
      />
    </motion.div>
  );
}
